import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container, Card, CardContent, Typography, Box, CircularProgress, Alert,
  Chip, IconButton, TextField, InputAdornment, Select, MenuItem,
  FormControl, InputLabel, Tooltip, Divider, Button, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, RadioGroup,
  FormControlLabel, Radio, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useReactToPrint } from 'react-to-print';
import { API_URL } from '../../config';

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  'UNDER PROCESS': {
    label: 'Under Process',
    color: '#b45309',
    bg: '#fffbeb',
    border: '#fcd34d',
    dot: '#d97706',
  },
  'NOT PROCESSED': {
    label: 'Not Processed',
    color: '#b91c1c',
    bg: '#fff1f2',
    border: '#fca5a5',
    dot: '#dc2626',
  },
  'LOADING CURRENTMONTH': {
    label: 'Loading This Month',
    color: '#166534',
    bg: '#f0fdf4',
    border: '#86efac',
    dot: '#16a34a',
  },
};

const ALL_STATUSES = Object.keys(STATUS_CONFIG);

// ─── Sort months chronologically (MMM-yyyy) ───────────────────────────────────
const MONTH_ORDER = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function parseMonthYear(str) {
  // e.g. "Apr-2026"
  if (!str || str === 'UNKNOWN') return new Date(9999, 0);
  const parts = str.split('-');
  if (parts.length !== 2) return new Date(9999, 0);
  const mIdx = MONTH_ORDER.findIndex(m => m.toLowerCase() === parts[0].toLowerCase());
  const yr = parseInt(parts[1], 10);
  if (mIdx === -1 || isNaN(yr)) return new Date(9999, 0);
  return new Date(yr, mIdx);
}

function sortMonths(months) {
  return [...months].sort((a, b) => parseMonthYear(a) - parseMonthYear(b));
}

// ─── Sub-order chip ───────────────────────────────────────────────────────────
const SubOrderChip = ({ row, onEditClick }) => {
  const cfg = STATUS_CONFIG[row.CURRENTSTATUS] || STATUS_CONFIG['NOT PROCESSED'];

  const tooltipContent = (
    <Box sx={{
      p: 1.5,
      minWidth: 220,
      backgroundColor: '#fff',
      borderRadius: 2,
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      border: '1px solid #e5e7eb',
    }}>
      {/* Status badge at top */}
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.7,
        px: 1.2, py: 0.4, borderRadius: '20px', mb: 1.5,
        backgroundColor: cfg.bg,
        border: `1.5px solid ${cfg.border}`,
      }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: cfg.dot }} />
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: cfg.color }}>
          {cfg.label}
        </Typography>
      </Box>

      {/* Fields */}
      {[
        { label: 'Sub Order', value: row.SUBORDERNO },
        { label: 'Container', value: row.CONTAINER_NO || '—' },
        { label: 'Order No', value: row.ORDERNO },
        { label: 'Client', value: row.Client_Name },
        { label: 'Consignee', value: row.IST_NOTIFY_CONSIGNEE_NAME },
        { label: 'Delivery', value: row.DELIVERYMONTH },
        { label: 'Total CBM', value: row.TOTAL_CBM != null ? Number(row.TOTAL_CBM).toFixed(3) : '—' },
        { label: 'Containers', value: row.CONTAINER_COUNT != null ? Math.round(row.CONTAINER_COUNT) : '—' },
      ].map(({ label, value }) => (
        <Box key={label} sx={{ display: 'flex', gap: 1, mb: 0.6, alignItems: 'flex-start' }}>
          <Typography sx={{
            fontSize: '0.72rem', fontWeight: 700,
            color: '#9ca3af', minWidth: 68, flexShrink: 0,
            textTransform: 'uppercase', letterSpacing: 0.3, pt: 0.1
          }}>
            {label}
          </Typography>
          <Typography sx={{
            fontSize: '0.8rem', fontWeight: 500,
            color: '#111827', lineHeight: 1.3, wordBreak: 'break-word'
          }}>
            {value}
          </Typography>
        </Box>
      ))}

      {/* Tap hint */}
      <Box sx={{
        mt: 1.2, pt: 1, borderTop: '1px solid #f3f4f6',
        display: 'flex', alignItems: 'center', gap: 0.5
      }}>
        <EditIcon sx={{ fontSize: 11, color: '#9ca3af' }} />
        <Typography sx={{ fontSize: '0.7rem', color: '#9ca3af', fontStyle: 'italic' }}>
          Tap to change status
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      arrow={false}
      placement="top"
      enterDelay={0}
      enterNextDelay={0}
      leaveDelay={0}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'transparent',
            p: 0,
            boxShadow: 'none',
            maxWidth: 280,
          }
        }
      }}
    >
      <Box
        onClick={() => onEditClick(row)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          px: 1.2,
          borderRadius: '6px',
          border: `1.5px solid ${cfg.border}`,
          backgroundColor: cfg.bg,
          cursor: 'pointer',
          width: 160,
          minHeight: 36,
          flexShrink: 0,
          overflow: 'hidden',
          '&:hover': {
            filter: 'brightness(0.93)',
            boxShadow: `0 2px 8px ${cfg.border}99`,
          },
          userSelect: 'none',
        }}
      >
        <Box sx={{
          width: 7, height: 7, borderRadius: '50%',
          backgroundColor: cfg.dot, flexShrink: 0
        }} />
        <Typography sx={{
          fontSize: '0.78rem', fontWeight: 600,
          color: cfg.color, lineHeight: 1.2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
        }}>
          {row.SUBORDERNO}
        </Typography>
        <EditIcon sx={{ fontSize: 11, color: cfg.color, opacity: 0.55, flexShrink: 0 }} />
      </Box>
    </Tooltip>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────────
const OrderTrackingViewV2 = ({ onBackClick }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [details, setDetails] = useState([]);
  const [summary, setSummary] = useState([]);
  const [summaryFlat, setSummaryFlat] = useState([]);

  const [searchText, setSearchText] = useState('');
  const [filterMarket, setFilterMarket] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterMonth, setFilterMonth] = useState('ALL');

  const [editDialog, setEditDialog] = useState({ open: false, row: null, newStatus: '' });
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Order_Tracking_${new Date().toISOString().split('T')[0]}`,
  });

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/order/trackingv2`);
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();
      setDetails(apiData.data?.[0] || []);
      setSummary(apiData.data?.[1] || []);
      // recordset 2 = flat summary with CBM + containers
      setSummaryFlat(apiData.data?.[2] || []);
    } catch (e) {
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Suppress unused warning — summary kept for future use
  void summary;

  const markets = useMemo(() =>
    ['ALL', ...Array.from(new Set(details.map(r => r.MARKET).filter(Boolean))).sort()],
    [details]
  );

  const months = useMemo(() => {
    const unique = Array.from(new Set(details.map(r => r.DELIVERYMONTH).filter(Boolean)));
    return ['ALL', ...sortMonths(unique)];
  }, [details]);

  const filteredDetails = useMemo(() => {
    const q = searchText.toLowerCase();
    return details.filter(r => {
      if (filterMarket !== 'ALL' && r.MARKET !== filterMarket) return false;
      if (filterStatus !== 'ALL' && r.CURRENTSTATUS !== filterStatus) return false;
      if (filterMonth !== 'ALL' && r.DELIVERYMONTH !== filterMonth) return false;
      if (q && !(
        (r.SUBORDERNO || '').toLowerCase().includes(q) ||
        (r.ORDERNO || '').toLowerCase().includes(q) ||
        (r.CONTAINER_NO || '').toLowerCase().includes(q) ||
        (r.Client_Name || '').toLowerCase().includes(q) ||
        (r.IST_NOTIFY_CONSIGNEE_NAME || '').toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [details, filterMarket, filterStatus, filterMonth, searchText]);

  // Group: market → deliveryMonth (sorted) → rows
  const grouped = useMemo(() => {
    const map = {};
    filteredDetails.forEach(r => {
      const mkt = r.MARKET || 'UNKNOWN';
      const mon = r.DELIVERYMONTH || 'UNKNOWN';
      if (!map[mkt]) map[mkt] = {};
      if (!map[mkt][mon]) map[mkt][mon] = [];
      map[mkt][mon].push(r);
    });
    return map;
  }, [filteredDetails]);

  // All months across ALL markets (not just filtered) — so every market shows same columns
  const allMonthsGlobal = useMemo(() => {
    const unique = Array.from(new Set(details.map(r => r.DELIVERYMONTH).filter(Boolean)));
    return sortMonths(unique);
  }, [details]);

  const grandTotals = useMemo(() => {
    const t = ALL_STATUSES.reduce((a, s) => { a[s] = 0; return a; }, {});
    filteredDetails.forEach(r => { if (t[r.CURRENTSTATUS] !== undefined) t[r.CURRENTSTATUS]++; });
    t.Total = filteredDetails.length;
    return t;
  }, [filteredDetails]);

  const handleExcelDownload = async () => {
    const wb = new ExcelJS.Workbook();
    wb.creator = 'Plywood Software Report';
    wb.created = new Date();

    // ── Colour palette ──────────────────────────────────────────────────────
    const GREEN_DARK  = '1B4332';
    const GREEN_MID   = '2D6A4F';
    const GREEN_LIGHT = 'D1FAE5';
    const ORANGE_BG   = 'FFFBEB';
    const ORANGE_FG   = 'B45309';
    const ORANGE_HDR  = 'FCD34D';
    const RED_BG      = 'FFF1F2';
    const RED_FG      = 'B91C1C';
    const RED_HDR     = 'FCA5A5';
    const TEAL_BG     = 'F0FDF4';
    const TEAL_FG     = '166534';
    const TEAL_HDR    = '86EFAC';
    const GREY_ROW    = 'F8FAFB';
    const WHITE       = 'FFFFFF';

    const statusColor = (status) => {
      if (status === 'UNDER PROCESS')       return { bg: ORANGE_BG, fg: ORANGE_FG, accent: ORANGE_HDR };
      if (status === 'NOT PROCESSED')       return { bg: RED_BG,    fg: RED_FG,    accent: RED_HDR    };
      if (status === 'LOADING CURRENTMONTH') return { bg: TEAL_BG,  fg: TEAL_FG,   accent: TEAL_HDR   };
      return { bg: WHITE, fg: '374151', accent: 'D1D5DB' };
    };

    const applyBorder = (cell) => {
      cell.border = {
        top:    { style: 'thin', color: { argb: 'E5E7EB' } },
        left:   { style: 'thin', color: { argb: 'E5E7EB' } },
        bottom: { style: 'thin', color: { argb: 'E5E7EB' } },
        right:  { style: 'thin', color: { argb: 'E5E7EB' } },
      };
    };

    // ════════════════════════════════════════════════════════════════════════
    // SHEET 1 — DETAIL
    // ════════════════════════════════════════════════════════════════════════
    const ws1 = wb.addWorksheet('Detail', { views: [{ state: 'frozen', ySplit: 3 }] });

    // Title row
    ws1.mergeCells('A1:I1');
    const titleCell = ws1.getCell('A1');
    titleCell.value = 'ORDER TRACKING — DETAIL';
    titleCell.font  = { bold: true, size: 14, color: { argb: WHITE }, name: 'Calibri' };
    titleCell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN_DARK } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    ws1.getRow(1).height = 32;

    // Sub-title row
    ws1.mergeCells('A2:I2');
    const subCell = ws1.getCell('A2');
    subCell.value = `Generated: ${new Date().toLocaleString()}   |   Total rows: ${details.length}`;
    subCell.font  = { size: 10, color: { argb: WHITE }, italic: true };
    subCell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN_MID } };
    subCell.alignment = { horizontal: 'center', vertical: 'middle' };
    ws1.getRow(2).height = 20;

    // Header row
    const d1Headers = ['Sub Order No', 'Order No', 'Container No', 'Client', 'Consignee', 'Market', 'Delivery Month', 'Delivery Date', 'Status', 'Total CBM', 'Containers'];
    const d1Widths  = [20, 18, 18, 28, 28, 18, 16, 16, 22, 14, 14];
    ws1.columns = d1Widths.map((w, i) => ({ key: String(i), width: w }));

    const hdrRow1 = ws1.getRow(3);
    d1Headers.forEach((h, i) => {
      const cell = hdrRow1.getCell(i + 1);
      cell.value = h;
      cell.font  = { bold: true, size: 10, color: { argb: WHITE }, name: 'Calibri' };
      cell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFA726' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      applyBorder(cell);
    });
    hdrRow1.height = 24;

    // Data rows
    details.forEach((row, i) => {
      const sc   = statusColor(row.CURRENTSTATUS);
      const isOdd = i % 2 === 0;
      const exRow = ws1.addRow([
        row.SUBORDERNO || '',
        row.ORDERNO || '',
        row.CONTAINER_NO || '',
        row.Client_Name || '',
        row.IST_NOTIFY_CONSIGNEE_NAME || '',
        row.MARKET || '',
        row.DELIVERYMONTH || '',
        row.DELIVERY_TIME ? new Date(row.DELIVERY_TIME).toLocaleDateString() : '',
        STATUS_CONFIG[row.CURRENTSTATUS]?.label || row.CURRENTSTATUS || '',
        row.TOTAL_CBM != null ? Number(row.TOTAL_CBM).toFixed(3) : '',
        row.CONTAINER_COUNT != null ? Math.round(row.CONTAINER_COUNT) : '',
      ]);
      exRow.height = 18;

      exRow.eachCell((cell, colNum) => {
        const isStatusCol = colNum === 9;
        cell.font = {
          size: 10,
          bold: isStatusCol,
          color: { argb: isStatusCol ? sc.fg : '374151' },
          name: 'Calibri'
        };
        cell.fill = {
          type: 'pattern', pattern: 'solid',
          fgColor: { argb: isStatusCol ? sc.bg : (isOdd ? WHITE : GREY_ROW) }
        };
        cell.alignment = { vertical: 'middle', horizontal: colNum <= 2 ? 'left' : 'left' };
        applyBorder(cell);
      });

      // Status cell gets a coloured left border accent
      const statusCell = exRow.getCell(9);
      statusCell.border = {
        ...statusCell.border,
        left: { style: 'medium', color: { argb: sc.accent } }
      };
    });

    // Auto-filter on header row
    ws1.autoFilter = { from: { row: 3, column: 1 }, to: { row: 3, column: 9 } };

    // ════════════════════════════════════════════════════════════════════════
    // SHEET 2 — SUMMARY
    // ════════════════════════════════════════════════════════════════════════
    const ws2 = wb.addWorksheet('Summary by Market', { views: [{ state: 'frozen', ySplit: 3 }] });

    // Title
    ws2.mergeCells('A1:F1');
    const t2 = ws2.getCell('A1');
    t2.value = 'ORDER TRACKING — SUMMARY BY MARKET';
    t2.font  = { bold: true, size: 14, color: { argb: WHITE }, name: 'Calibri' };
    t2.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN_DARK } };
    t2.alignment = { horizontal: 'center', vertical: 'middle' };
    ws2.getRow(1).height = 32;

    // Sub-title
    ws2.mergeCells('A2:F2');
    const s2 = ws2.getCell('A2');
    s2.value = `Generated: ${new Date().toLocaleString()}`;
    s2.font  = { size: 10, color: { argb: WHITE }, italic: true };
    s2.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN_MID } };
    s2.alignment = { horizontal: 'center', vertical: 'middle' };
    ws2.getRow(2).height = 20;

    // Headers
    ws2.columns = [
      { key: 'market', width: 24 },
      { key: 'under',  width: 18 },
      { key: 'not',    width: 18 },
      { key: 'loading',width: 22 },
      { key: 'total',  width: 12 },
    ];

    const s2Headers = [
      { label: 'Market',             bg: 'FFA726' },
      { label: 'Under Process',      bg: ORANGE_HDR },
      { label: 'Not Processed',      bg: RED_HDR    },
      { label: 'Loading This Month', bg: TEAL_HDR   },
      { label: 'Total',              bg: 'A7F3D0'   },
    ];

    const hdrRow2 = ws2.getRow(3);
    s2Headers.forEach(({ label, bg }, i) => {
      const cell = hdrRow2.getCell(i + 1);
      cell.value = label;
      cell.font  = { bold: true, size: 10, color: { argb: '1F2937' }, name: 'Calibri' };
      cell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      applyBorder(cell);
    });
    hdrRow2.height = 24;

    // Data rows
    summary.forEach((row, i) => {
      const isOdd = i % 2 === 0;
      const exRow = ws2.addRow([
        row.MARKET || '',
        row['UNDER PROCESS'] || 0,
        row['NOT PROCESSED'] || 0,
        row['LOADING CURRENTMONTH'] || 0,
        row.Total || 0,
      ]);
      exRow.height = 20;

      exRow.eachCell((cell, colNum) => {
        const isTotal = colNum === 5;
        cell.font = {
          size: 10, bold: isTotal || colNum === 1,
          color: { argb: colNum === 1 ? GREEN_DARK : isTotal ? GREEN_DARK : '374151' },
          name: 'Calibri'
        };
        cell.fill = {
          type: 'pattern', pattern: 'solid',
          fgColor: { argb: isOdd ? WHITE : GREY_ROW }
        };
        cell.alignment = { vertical: 'middle', horizontal: colNum === 1 ? 'left' : 'center' };
        applyBorder(cell);
      });

      // Colour-code the count cells
      const underCell   = exRow.getCell(2);
      const notCell     = exRow.getCell(3);
      const loadCell    = exRow.getCell(4);
      const totalCell   = exRow.getCell(5);

      if ((row['UNDER PROCESS'] || 0) > 0) {
        underCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ORANGE_BG } };
        underCell.font = { ...underCell.font, color: { argb: ORANGE_FG }, bold: true };
      }
      if ((row['NOT PROCESSED'] || 0) > 0) {
        notCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: RED_BG } };
        notCell.font = { ...notCell.font, color: { argb: RED_FG }, bold: true };
      }
      if ((row['LOADING CURRENTMONTH'] || 0) > 0) {
        loadCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TEAL_BG } };
        loadCell.font = { ...loadCell.font, color: { argb: TEAL_FG }, bold: true };
      }
      totalCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GREEN_LIGHT } };
      totalCell.font = { ...totalCell.font, color: { argb: GREEN_DARK }, bold: true, size: 11 };
    });

    ws2.autoFilter = { from: { row: 3, column: 1 }, to: { row: 3, column: 5 } };

    // ── Write & download ────────────────────────────────────────────────────
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf], { type: 'application/octet-stream' }),
      `Order_Tracking_${new Date().toISOString().split('T')[0]}.xlsx`
    );
  };

  const openEditDialog = (row) => {
    setEditDialog({ open: true, row, newStatus: row.CURRENTSTATUS });
    setHistory([]);
    setHistoryLoading(true);
    fetch(`${API_URL}/order/tracking/history/${encodeURIComponent(row.SUBORDERNO)}`)
      .then(r => r.json())
      .then(d => setHistory(d.data || []))
      .catch(() => setHistory([]))
      .finally(() => setHistoryLoading(false));
  };

  const handleStatusUpdate = async () => {
    const { row, newStatus } = editDialog;
    if (!newStatus || newStatus === row.CURRENTSTATUS) {
      setEditDialog({ open: false, row: null, newStatus: '' });
      return;
    }
    setUpdating(true);
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const res = await fetch(`${API_URL}/order/tracking/updatestatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subOrderNo: row.SUBORDERNO,
          containerNo: row.CONTAINER_NO,
          orderNo: row.ORDERNO,
          newStatus,
          oldStatus: row.CURRENTSTATUS,
          market: row.MARKET,
          changedBy: user.username || 'unknown',
        }),
      });
      if (!res.ok) throw new Error('Update failed: ' + res.statusText);

      // ── Optimistic update: patch local state instantly, no waiting for full reload ──
      setDetails(prev => prev.map(r =>
        r.SUBORDERNO === row.SUBORDERNO && r.ORDERNO === row.ORDERNO
          ? { ...r, CURRENTSTATUS: newStatus }
          : r
      ));

      setSnackbar({ open: true, message: 'Status updated', severity: 'success' });
      setEditDialog({ open: false, row: null, newStatus: '' });

      // Silent background refresh to sync any server-side changes
      fetchData();
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Update failed', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 6, px: { xs: 1, md: 2 } }}>

      {/* ── Header ── */}
      <Card sx={{
        mb: 2.5, borderRadius: 3,
        boxShadow: '0 2px 8px rgba(27,67,50,0.08)',
        border: '1px solid #e8f5e9',
        background: '#fff'
      }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            {onBackClick && (
              <IconButton onClick={onBackClick} size="small" sx={{ color: '#1b4332' }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{
              background: '#1b4332',
              borderRadius: 2, p: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <TrackChangesIcon sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1b4332', lineHeight: 1.2 }}>
                Order Tracking
              </Typography>
              <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                Pending orders by market · Click any order to change its status
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
              onClick={fetchData}
              disabled={loading}
              sx={{
                color: '#1b4332', borderColor: '#d1d5db', textTransform: 'none',
                fontWeight: 500, fontSize: '0.82rem',
                '&:hover': { borderColor: '#1b4332', backgroundColor: '#f0fdf4' }
              }}
            >
              Refresh
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
              onClick={handlePrint}
              disabled={loading || details.length === 0}
              sx={{
                color: '#1b4332', borderColor: '#d1d5db', textTransform: 'none',
                fontWeight: 500, fontSize: '0.82rem',
                '&:hover': { borderColor: '#1b4332', backgroundColor: '#f0fdf4' }
              }}
            >
              Export PDF
            </Button>
          </Box>

          {/* Colour legend — inline, compact */}
          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            {ALL_STATUSES.map(s => {
              const cfg = STATUS_CONFIG[s];
              return (
                <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: cfg.dot }} />
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500 }}>
                    {cfg.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* ── Filters ── */}
      <Box sx={{ mb: 2.5, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 1.5 }}>
        <TextField
          size="small"
          placeholder="Search order, container, client…"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17, color: '#9ca3af' }} />
                </InputAdornment>
              )
            }
          }}
          sx={{ width: { xs: '100%', sm: 'auto' }, flexGrow: 1, maxWidth: { sm: 360 }, backgroundColor: '#fff', borderRadius: 1 }}
        />
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 160, backgroundColor: '#fff' }}>
            <InputLabel>Market</InputLabel>
            <Select value={filterMarket} label="Market" onChange={e => setFilterMarket(e.target.value)}>
              {markets.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150, backgroundColor: '#fff' }}>
            <InputLabel>Month</InputLabel>
            <Select value={filterMonth} label="Month" onChange={e => setFilterMonth(e.target.value)}>
              {months.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200, backgroundColor: '#fff' }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
              <MenuItem value="ALL">All</MenuItem>
              {ALL_STATUSES.map(s => (
                <MenuItem key={s} value={s}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: STATUS_CONFIG[s].dot, flexShrink: 0 }} />
                    <span style={{ fontSize: '0.85rem' }}>{STATUS_CONFIG[s].label}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* ── Loading / Error ── */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: '#1b4332' }} size={36} />
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Empty state ── */}
      {!loading && !error && Object.keys(grouped).length === 0 && (
        <Card sx={{ borderRadius: 2, p: 5, textAlign: 'center', border: '1px dashed #e5e7eb' }}>
          <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
            No orders match the current filters.
          </Typography>
        </Card>
      )}

      {/* ── Market blocks ── */}
      {!loading && !error && Object.keys(grouped).sort().map(market => {
        const monthMap = grouped[market];
        const allMarketRows = Object.values(monthMap).flat();

        return (
          <Card key={market} sx={{
            mb: 2.5, borderRadius: 3,
            boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
            border: '1px solid #e5e7eb',
            overflow: 'hidden'
          }}>
            {/* Market header bar */}
            <Box sx={{
              px: { xs: 2, md: 3 }, py: { xs: 1.5, md: 2 },
              background: 'linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%)',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 1, sm: 1.5 }
            }}>
              <Typography sx={{
                fontWeight: 800, color: '#fff',
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                letterSpacing: 0.5, lineHeight: 1.1
              }}>
                {market}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', alignItems: 'center' }}>
                {ALL_STATUSES.map(s => {
                  const cnt = allMarketRows.filter(r => r.CURRENTSTATUS === s).length;
                  if (cnt === 0) return null;
                  return (
                    <Chip key={s}
                      label={`${STATUS_CONFIG[s].label}: ${cnt}`}
                      size="small"
                      sx={{
                        backgroundColor: STATUS_CONFIG[s].bg,
                        color: STATUS_CONFIG[s].color,
                        border: `1px solid ${STATUS_CONFIG[s].border}`,
                        fontWeight: 700,
                        fontSize: { xs: '0.68rem', md: '0.75rem' },
                        height: { xs: 22, md: 24 }
                      }}
                    />
                  );
                })}
                <Chip
                  label={`Total: ${allMarketRows.length}`}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: '#fff', fontWeight: 800,
                    fontSize: { xs: '0.68rem', md: '0.78rem' },
                    height: { xs: 22, md: 24 },
                    border: '1px solid rgba(255,255,255,0.3)'
                  }}
                />
              </Box>
            </Box>

            {/* Months as columns — horizontally scrollable */}
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{
                borderCollapse: 'collapse',
                width: '100%',
                minWidth: `${allMonthsGlobal.length * 180}px`
              }}>
                <thead>
                  <tr>
                    {allMonthsGlobal.map(month => (
                      <th key={month} style={{
                        padding: '10px 12px',
                        borderRight: '1px solid #e5e7eb',
                        borderBottom: '2px solid #1b4332',
                        backgroundColor: '#f0fdf4',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        minWidth: 170,
                      }}>
                        <span style={{ fontSize: '1rem', fontWeight: 700, color: '#1b4332' }}>
                          {month}
                        </span>
                        <br />
                        <span style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 500 }}>
                          {(monthMap[month] || []).length} order{(monthMap[month] || []).length !== 1 ? 's' : ''}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {allMonthsGlobal.map(month => {
                      const rows = monthMap[month] || [];
                      return (
                        <td key={month} style={{
                          padding: '10px 10px',
                          borderRight: '1px solid #e5e7eb',
                          verticalAlign: 'top',
                          backgroundColor: '#fff',
                        }}>
                          {rows.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 1 }}>
                              <Typography sx={{ fontSize: '0.8rem', color: '#d1d5db' }}>—</Typography>
                            </Box>
                          ) : (
                            <Box sx={{
                              display: 'flex', flexWrap: 'wrap',
                              gap: 0.7,
                              alignItems: 'flex-start', alignContent: 'flex-start'
                            }}>
                              {rows.map((row, idx) => (
                                <SubOrderChip
                                  key={`${row.SUBORDERNO}-${idx}`}
                                  row={row}
                                  onEditClick={openEditDialog}
                                />
                              ))}
                            </Box>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </Box>
          </Card>
        );
      })}

      {/* ── Grand Total ── */}
      {!loading && !error && filteredDetails.length > 0 && (
        <Box sx={{
          mt: 1, p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '2px solid #bbf7d0',
          backgroundColor: '#f0fdf4',
          display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center'
        }}>
          <Typography sx={{
            fontSize: '0.85rem', fontWeight: 800,
            color: '#166534', mr: 0.5, letterSpacing: 0.4,
            textTransform: 'uppercase'
          }}>
            Grand Total
          </Typography>
          {ALL_STATUSES.map(s => (
            grandTotals[s] > 0 && (
              <Box key={s} sx={{
                display: 'flex', alignItems: 'center', gap: 0.7,
                px: 1.5, py: 0.5, borderRadius: '6px',
                backgroundColor: STATUS_CONFIG[s].bg,
                border: `1.5px solid ${STATUS_CONFIG[s].border}`
              }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: STATUS_CONFIG[s].dot }} />
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: STATUS_CONFIG[s].color }}>
                  {STATUS_CONFIG[s].label}:{' '}
                  <span style={{ fontSize: '1rem', fontWeight: 800 }}>{grandTotals[s]}</span>
                </Typography>
              </Box>
            )
          ))}
          <Box sx={{
            px: 1.5, py: 0.5, borderRadius: '6px',
            backgroundColor: '#1b4332'
          }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff' }}>
              Total:{' '}
              <span style={{ fontSize: '1rem', fontWeight: 800 }}>{grandTotals.Total}</span>
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Data Tables ── */}
      {!loading && !error && details.length > 0 && (
        <Card sx={{
          mt: 3, borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #e8f5e9',
          overflow: 'hidden'
        }}>
          {/* Table header bar */}
          <Box sx={{
            background: 'linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%)',
            px: { xs: 2, md: 3 }, py: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5
          }}>
            <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '1rem' }}>
              Raw Data
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExcelDownload}
              sx={{
                backgroundColor: '#fff', color: '#1b4332',
                fontWeight: 600, textTransform: 'none', fontSize: '0.82rem',
                '&:hover': { backgroundColor: '#f0fdf4' }
              }}
            >
              Download Excel
            </Button>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: '1px solid #e5e7eb' }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9rem', color: '#666' },
                '& .MuiTab-root.Mui-selected': { color: '#1b4332' },
                '& .MuiTabs-indicator': { backgroundColor: '#1b4332', height: 3 }
              }}
            >
              <Tab label={`Detail (${details.length} rows)`} />
              <Tab label={`Summary by Market (${summary.length} rows)`} />
            </Tabs>
          </Box>

          {/* Tab 0 — Detail */}
          {activeTab === 0 && (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {['Sub Order', 'Order No', 'Container', 'Client', 'Consignee', 'Market', 'Delivery Month', 'Status', 'Total CBM', 'Containers'].map(h => (
                      <TableCell key={h} sx={{
                        fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase',
                        letterSpacing: 0.4, color: '#fff', py: 1.5,
                        background: 'linear-gradient(135deg, #ffb74d 0%, #ffa726 100%)',
                        borderRight: '1px solid rgba(255,255,255,0.2)', whiteSpace: 'nowrap'
                      }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.map((row, i) => {
                    const cfg = STATUS_CONFIG[row.CURRENTSTATUS] || STATUS_CONFIG['NOT PROCESSED'];
                    return (
                      <TableRow key={i} sx={{
                        backgroundColor: i % 2 === 0 ? '#fff' : '#f8faf9',
                        '&:hover': { backgroundColor: '#f0fdf4' }
                      }}>
                        <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1b4332', whiteSpace: 'nowrap' }}>{row.SUBORDERNO}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem', color: '#374151', whiteSpace: 'nowrap' }}>{row.ORDERNO}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem', color: '#374151', whiteSpace: 'nowrap' }}>{row.CONTAINER_NO || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem', color: '#374151' }}>{row.Client_Name}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem', color: '#374151' }}>{row.IST_NOTIFY_CONSIGNEE_NAME}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem', color: '#374151', whiteSpace: 'nowrap' }}>{row.MARKET}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem', color: '#374151', whiteSpace: 'nowrap' }}>{row.DELIVERYMONTH}</TableCell>
                        <TableCell>
                          <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                            px: 1, py: 0.3, borderRadius: '5px',
                            backgroundColor: cfg.bg, border: `1px solid ${cfg.border}`
                          }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: cfg.dot }} />
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: cfg.color, whiteSpace: 'nowrap' }}>
                              {cfg.label}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.82rem', textAlign: 'right', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
                          {row.TOTAL_CBM != null ? Number(row.TOTAL_CBM).toFixed(3) : '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.82rem', textAlign: 'center', fontWeight: 700, color: '#1b4332', whiteSpace: 'nowrap' }}>
                          {row.CONTAINER_COUNT != null ? Math.round(row.CONTAINER_COUNT) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Tab 1 — Summary with CBM + Containers */}
          {activeTab === 1 && (() => {
            // Build pivot from flat data (recordset 3)
            const markets = [...new Set(summaryFlat.map(r => r.MARKET))].sort();
            const statuses = ['UNDER PROCESS', 'NOT PROCESSED', 'LOADING CURRENTMONTH'];
            const flat = {};
            summaryFlat.forEach(r => {
              if (!flat[r.MARKET]) flat[r.MARKET] = {};
              flat[r.MARKET][r.CURRENTSTATUS] = r;
            });
            const hdr = { fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4, color: '#fff', py: 1.2, textAlign: 'center', background: 'linear-gradient(135deg, #ffb74d 0%, #ffa726 100%)', borderRight: '1px solid rgba(255,255,255,0.2)', whiteSpace: 'nowrap' };
            return (
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
                  <thead>
                    <tr>
                      <th rowSpan={2} style={{ ...hdr, textAlign: 'left', px: 2, minWidth: 140, verticalAlign: 'middle', padding: '8px 12px' }}>Market</th>
                      {statuses.map(s => (
                        <th key={s} colSpan={3} style={{ ...hdr, padding: '6px 8px', backgroundColor: STATUS_CONFIG[s].bg, color: STATUS_CONFIG[s].color, border: `1px solid ${STATUS_CONFIG[s].border}` }}>
                          {STATUS_CONFIG[s].label}
                        </th>
                      ))}
                      <th colSpan={3} style={{ ...hdr, padding: '6px 8px', backgroundColor: '#1b4332' }}>Total</th>
                    </tr>
                    <tr>
                      {statuses.map(s => ['Orders','CBM','Ctns'].map(lbl => (
                        <th key={s+lbl} style={{ ...hdr, padding: '5px 8px', fontSize: '0.68rem', backgroundColor: STATUS_CONFIG[s].bg, color: STATUS_CONFIG[s].color, border: `1px solid ${STATUS_CONFIG[s].border}` }}>{lbl}</th>
                      )))}
                      {['Orders','CBM','Ctns'].map(lbl => (
                        <th key={'tot'+lbl} style={{ ...hdr, padding: '5px 8px', fontSize: '0.68rem', backgroundColor: '#1b4332' }}>{lbl}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {markets.map((mkt, i) => {
                      const d = flat[mkt] || {};
                      const totOrders = statuses.reduce((s, st) => s + (d[st]?.ORDERCOUNT || 0), 0);
                      const totCBM    = statuses.reduce((s, st) => s + (d[st]?.TOTAL_CBM || 0), 0);
                      const totCtns   = statuses.reduce((s, st) => s + (d[st]?.CONTAINER_COUNT || 0), 0);
                      return (
                        <tr key={mkt} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f8faf9' }}>
                          <td style={{ padding: '7px 12px', fontWeight: 700, color: '#1b4332', fontSize: '0.85rem', borderBottom: '1px solid #f0f0f0', borderRight: '2px solid #e5e7eb' }}>{mkt}</td>
                          {statuses.map(s => [
                            <td key={s+'o'} style={{ padding: '7px 8px', textAlign: 'center', fontWeight: 600, color: STATUS_CONFIG[s].color, fontSize: '0.85rem', borderBottom: '1px solid #f0f0f0', borderRight: '1px solid #e5e7eb' }}>{d[s]?.ORDERCOUNT || 0}</td>,
                            <td key={s+'c'} style={{ padding: '7px 8px', textAlign: 'right', fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f0f0f0', borderRight: '1px solid #e5e7eb' }}>{d[s]?.TOTAL_CBM ? Number(d[s].TOTAL_CBM).toFixed(1) : '—'}</td>,
                            <td key={s+'n'} style={{ padding: '7px 8px', textAlign: 'center', fontSize: '0.82rem', color: '#374151', borderBottom: '1px solid #f0f0f0', borderRight: '2px solid #e5e7eb' }}>{d[s]?.CONTAINER_COUNT ? Math.round(d[s].CONTAINER_COUNT) : '—'}</td>
                          ])}
                          <td style={{ padding: '7px 8px', textAlign: 'center', fontWeight: 800, color: '#1b4332', fontSize: '0.9rem', borderBottom: '1px solid #f0f0f0', borderRight: '1px solid #e5e7eb' }}>{totOrders}</td>
                          <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 700, color: '#1b4332', fontSize: '0.85rem', borderBottom: '1px solid #f0f0f0', borderRight: '1px solid #e5e7eb' }}>{totCBM.toFixed(1)}</td>
                          <td style={{ padding: '7px 8px', textAlign: 'center', fontWeight: 700, color: '#1b4332', fontSize: '0.85rem', borderBottom: '1px solid #f0f0f0' }}>{Math.round(totCtns)}</td>
                        </tr>
                      );
                    })}
                    {/* Grand total */}
                    {markets.length > 0 && (() => {
                      const gOrders = summaryFlat.reduce((s, r) => s + (r.ORDERCOUNT || 0), 0);
                      const gCBM    = summaryFlat.reduce((s, r) => s + (r.TOTAL_CBM || 0), 0);
                      const gCtns   = summaryFlat.reduce((s, r) => s + (r.CONTAINER_COUNT || 0), 0);
                      const gtByStatus = statuses.reduce((acc, st) => {
                        acc[st] = {
                          ORDERCOUNT: summaryFlat.filter(r => r.CURRENTSTATUS === st).reduce((s, r) => s + (r.ORDERCOUNT || 0), 0),
                          TOTAL_CBM:  summaryFlat.filter(r => r.CURRENTSTATUS === st).reduce((s, r) => s + (r.TOTAL_CBM || 0), 0),
                          CONTAINER_COUNT: summaryFlat.filter(r => r.CURRENTSTATUS === st).reduce((s, r) => s + (r.CONTAINER_COUNT || 0), 0),
                        };
                        return acc;
                      }, {});
                      return (
                        <tr style={{ backgroundColor: '#1b4332' }}>
                          <td style={{ padding: '8px 12px', fontWeight: 800, color: '#fff', fontSize: '0.85rem', borderRight: '2px solid rgba(255,255,255,0.2)' }}>GRAND TOTAL</td>
                          {statuses.map(s => [
                            <td key={s+'o'} style={{ padding: '8px', textAlign: 'center', fontWeight: 800, color: STATUS_CONFIG[s].bg, fontSize: '0.9rem', borderRight: '1px solid rgba(255,255,255,0.15)' }}>{gtByStatus[s].ORDERCOUNT}</td>,
                            <td key={s+'c'} style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: '#fff', fontSize: '0.85rem', borderRight: '1px solid rgba(255,255,255,0.15)' }}>{gtByStatus[s].TOTAL_CBM.toFixed(1)}</td>,
                            <td key={s+'n'} style={{ padding: '8px', textAlign: 'center', fontWeight: 700, color: '#fff', fontSize: '0.85rem', borderRight: '2px solid rgba(255,255,255,0.2)' }}>{Math.round(gtByStatus[s].CONTAINER_COUNT)}</td>
                          ])}
                          <td style={{ padding: '8px', textAlign: 'center', fontWeight: 800, color: '#a7f3d0', fontSize: '1rem', borderRight: '1px solid rgba(255,255,255,0.15)' }}>{gOrders}</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 800, color: '#a7f3d0', fontSize: '0.9rem', borderRight: '1px solid rgba(255,255,255,0.15)' }}>{gCBM.toFixed(1)}</td>
                          <td style={{ padding: '8px', textAlign: 'center', fontWeight: 800, color: '#a7f3d0', fontSize: '0.9rem' }}>{Math.round(gCtns)}</td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </Box>
            );
          })()}
        </Card>
      )}

      {/* ── Edit Status Dialog ── */}
      <Dialog
        open={editDialog.open}
        onClose={() => !updating && setEditDialog({ open: false, row: null, newStatus: '' })}
        maxWidth="xs"
        fullWidth
        fullScreen={typeof window !== 'undefined' && window.innerWidth < 600}
        transitionDuration={0}
        slotProps={{ paper: { sx: { borderRadius: { xs: 0, sm: 3 } } } }}
      >
        <DialogTitle sx={{
          background: '#1b4332',
          color: '#fff', fontWeight: 700, fontSize: '0.95rem', py: 1.8
        }}>
          Change Status
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {editDialog.row && (
            <Box sx={{
              mb: 2, p: 1.5, borderRadius: 2,
              backgroundColor: '#f9fafb', border: '1px solid #e5e7eb'
            }}>
              <Typography variant="body2" sx={{ color: '#374151', mb: 0.3 }}>
                <b>Sub Order:</b> {editDialog.row.SUBORDERNO}
              </Typography>
              {editDialog.row.CONTAINER_NO && (
                <Typography variant="body2" sx={{ color: '#374151', mb: 0.3 }}>
                  <b>Container:</b> {editDialog.row.CONTAINER_NO}
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: '#374151', mb: 0.3 }}>
                <b>Order:</b> {editDialog.row.ORDERNO}
              </Typography>
              <Typography variant="body2" sx={{ color: '#374151' }}>
                <b>Delivery:</b> {editDialog.row.DELIVERYMONTH}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
            Select new status:
          </Typography>
          <RadioGroup
            value={editDialog.newStatus}
            onChange={e => setEditDialog(prev => ({ ...prev, newStatus: e.target.value }))}
          >
            {ALL_STATUSES.map(s => {
              const cfg = STATUS_CONFIG[s];
              const selected = editDialog.newStatus === s;
              return (
                <FormControlLabel
                  key={s}
                  value={s}
                  control={<Radio size="small" sx={{ color: cfg.dot, '&.Mui-checked': { color: cfg.dot } }} />}
                  label={
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: 1.2, py: 0.7, borderRadius: 2, width: '100%',
                      backgroundColor: selected ? cfg.bg : 'transparent',
                      border: selected ? `1.5px solid ${cfg.border}` : '1.5px solid transparent',
                      transition: 'all 0.12s ease'
                    }}>
                      <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: cfg.dot }} />
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: cfg.color }}>
                        {cfg.label}
                      </Typography>
                    </Box>
                  }
                  sx={{ mx: 0, mb: 0.5, width: '100%' }}
                />
              );
            })}
          </RadioGroup>

          {/* ── History timeline ── */}
          <Box sx={{ mt: 2.5 }}>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151', mb: 1.2, fontSize: '0.82rem', letterSpacing: 0.3 }}>
              CHANGE HISTORY
            </Typography>
            {historyLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={18} sx={{ color: '#1b4332' }} />
              </Box>
            )}
            {!historyLoading && history.length === 0 && (
              <Typography sx={{ fontSize: '0.78rem', color: '#9ca3af', fontStyle: 'italic' }}>
                No changes recorded yet.
              </Typography>
            )}
            {!historyLoading && history.map((h, i) => {
              const oldCfg = STATUS_CONFIG[h.OLD_STATUS] || { label: h.OLD_STATUS || '—', dot: '#9ca3af' };
              const newCfg = STATUS_CONFIG[h.NEW_STATUS] || { label: h.NEW_STATUS, dot: '#374151' };
              const changedAt = h.CHANGED_AT ? h.CHANGED_AT.toString() : null;
              // Format the raw server timestamp string directly — no timezone conversion
              const formatServerTime = (raw) => {
                // raw is like "2026-05-20T17:00:00.000Z" or "2026-05-20T17:00:00"
                // Strip the T and everything after seconds, display as-is from server
                const s = raw.replace('T', ' ').substring(0, 16); // "2026-05-20 17:00"
                const [datePart, timePart] = s.split(' ');
                const [yr, mo, dy] = datePart.split('-');
                return `${dy}/${mo}/${yr} ${timePart}`;
              };
              return (
                <Box key={h.SRNO} sx={{
                  display: 'flex', gap: 1.5, mb: 1.2,
                  pb: i < history.length - 1 ? 1.2 : 0,
                  borderBottom: i < history.length - 1 ? '1px solid #f3f4f6' : 'none'
                }}>
                  {/* Timeline dot + line */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.3 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: newCfg.dot, flexShrink: 0 }} />
                    {i < history.length - 1 && (
                      <Box sx={{ width: '1px', flex: 1, backgroundColor: '#e5e7eb', mt: 0.4 }} />
                    )}
                  </Box>
                  {/* Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '0.75rem', color: oldCfg.dot, fontWeight: 600, textDecoration: 'line-through' }}>
                        {oldCfg.label || '—'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af' }}>→</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: newCfg.dot, fontWeight: 700 }}>
                        {newCfg.label}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.3, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: '0.7rem', color: '#6b7280' }}>
                        by <b>{h.CHANGED_BY}</b>
                      </Typography>
                      {changedAt && (
                        <Typography sx={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                          {formatServerTime(changedAt)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setEditDialog({ open: false, row: null, newStatus: '' })}
            disabled={updating}
            sx={{ textTransform: 'none', color: '#6b7280', fontSize: '0.85rem' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleStatusUpdate}
            disabled={updating || !editDialog.newStatus || editDialog.newStatus === editDialog.row?.CURRENTSTATUS}
            startIcon={updating ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: '0.85rem',
              backgroundColor: '#1b4332',
              '&:hover': { backgroundColor: '#14532d' }
            }}
          >
            {updating ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ── Hidden Print Content ── */}
      <Box className="print-only" ref={printRef}>
        <style>{`
          @page { size: A4; margin: 8mm 8mm; }
          body { font-family: Arial, sans-serif; background: #fff; margin: 0; padding: 0; }
          .pt-page-break { page-break-before: always; break-before: page; }
          .pt-avoid { break-inside: avoid; page-break-inside: avoid; }
          .print-only { width: 100%; max-width: 100%; box-sizing: border-box; }
        `}</style>

        {/* ── Title ── */}
        <Box sx={{ borderBottom: '3px solid #000', pb: '10px', mb: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%' }}>
          <Box>
            <Typography sx={{ fontSize: '26px', fontWeight: 800, color: '#000', letterSpacing: 0.5, lineHeight: 1.1 }}>
              ORDER TRACKING REPORT
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#444', mt: '4px' }}>
              Generated: {new Date().toLocaleString()} &nbsp;·&nbsp; Total Sub-Orders: {details.length}
            </Typography>
          </Box>
          {/* Legend */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#000', textTransform: 'uppercase', letterSpacing: 0.5 }}>Legend</Typography>
            {[
              { bg: '#fff',  border: '1.5px solid #000', textColor: '#000', label: 'Under Process' },
              { bg: '#aaa',  border: '1.5px solid #888', textColor: '#000', label: 'Not Processed' },
              { bg: '#000',  border: '1.5px solid #000', textColor: '#fff', label: 'Loading This Month' },
            ].map(({ bg, border, textColor, label }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                <Box sx={{
                  width: '32px', height: '16px', borderRadius: '3px',
                  backgroundColor: bg, border
                }} />
                <Typography sx={{ fontSize: '11px', color: '#333' }}>{label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Market blocks ── */}
        {Object.keys(grouped).sort().map((market) => {
          const monthMap = grouped[market];
          const allMarketRows = Object.values(monthMap).flat();
          const sortedMonths = sortMonths(Object.keys(monthMap));
          const counts = ALL_STATUSES.reduce((a, s) => {
            a[s] = allMarketRows.filter(r => r.CURRENTSTATUS === s).length;
            return a;
          }, {});

          // B&W fill styles per status
          const chipStyle = {
            'UNDER PROCESS':       { bg: '#fff',  textColor: '#000', border: '1.5px solid #000' },
            'NOT PROCESSED':       { bg: '#aaa',  textColor: '#000', border: '1.5px solid #888' },
            'LOADING CURRENTMONTH':{ bg: '#000',  textColor: '#fff', border: '1.5px solid #000' },
          };

          return (
            <Box key={market} className="pt-avoid" sx={{ mb: '16px', width: '100%' }}>
              {/* Market header */}
              <Box sx={{
                borderTop: '2.5px solid #000',
                borderLeft: '2.5px solid #000',
                borderRight: '2.5px solid #000',
                borderBottom: '1.5px solid #000',
                px: '12px', py: '7px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                backgroundColor: '#f0f0f0'
              }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#000', letterSpacing: 0.4 }}>
                  {market}
                </Typography>
                <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  {ALL_STATUSES.map(s => counts[s] > 0 && (
                    <Box key={s} sx={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      px: '8px', py: '2px', borderRadius: '3px',
                      backgroundColor: chipStyle[s].bg,
                      border: chipStyle[s].border
                    }}>
                      <Typography sx={{ fontSize: '10px', fontWeight: 700, color: chipStyle[s].textColor }}>
                        {STATUS_CONFIG[s].label}: {counts[s]}
                      </Typography>
                    </Box>
                  ))}
                  <Typography sx={{ fontSize: '11px', fontWeight: 800, color: '#000' }}>
                    Total: {allMarketRows.length}
                  </Typography>
                </Box>
              </Box>

              {/* Month rows */}
              <Box sx={{ border: '1.5px solid #000', borderTop: 'none', width: '100%' }}>
                {sortedMonths.map((month, mIdx) => {
                  const rows = monthMap[month];
                  return (
                    <Box key={month} sx={{
                      display: 'flex', alignItems: 'flex-start',
                      borderBottom: mIdx < sortedMonths.length - 1 ? '1px solid #ccc' : 'none',
                      backgroundColor: mIdx % 2 === 0 ? '#fff' : '#f5f5f5'
                    }}>
                      {/* Month label */}
                      <Box sx={{
                        width: '80px', minWidth: '80px', flexShrink: 0,
                        borderRight: '1.5px solid #bbb',
                        px: '10px', py: '8px'
                      }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#000' }}>
                          {month}
                        </Typography>
                        <Typography sx={{ fontSize: '10px', color: '#555', mt: '2px' }}>
                          {rows.length} order{rows.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>

                      {/* Sub-order chips */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px', p: '7px 10px', flex: 1 }}>
                        {rows.map((row, idx) => {
                          const s = row.CURRENTSTATUS;
                          const cs = chipStyle[s] || chipStyle['UNDER PROCESS'];
                          return (
                            <Box key={idx} sx={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              px: '8px', py: '3px',
                              border: cs.border,
                              borderRadius: '3px',
                              backgroundColor: cs.bg
                            }}>
                              <Typography sx={{ fontSize: '10px', color: cs.textColor, lineHeight: 1, fontWeight: 700 }}>
                                {row.SUBORDERNO}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}

        {/* ── Summary table — new page ── */}
        {summary.length > 0 && (() => {
          const gt = {
            under:   summary.reduce((s, r) => s + (r['UNDER PROCESS'] || 0), 0),
            not:     summary.reduce((s, r) => s + (r['NOT PROCESSED'] || 0), 0),
            loading: summary.reduce((s, r) => s + (r['LOADING CURRENTMONTH'] || 0), 0),
            total:   summary.reduce((s, r) => s + (r.Total || 0), 0),
          };
          return (
            <Box className="pt-page-break">
              <Box sx={{ borderBottom: '3px solid #000', pb: '8px', mb: '16px' }}>
                <Typography sx={{ fontSize: '22px', fontWeight: 800, color: '#000' }}>
                  SUMMARY BY MARKET
                </Typography>
                <Typography sx={{ fontSize: '11px', color: '#444', mt: '3px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <Box sx={{ width: '20px', height: '12px', border: '1.5px solid #000', borderRadius: '2px', backgroundColor: '#fff', display: 'inline-block' }} />
                    Under Process
                  </Box>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <Box sx={{ width: '20px', height: '12px', border: '1.5px solid #888', borderRadius: '2px', backgroundColor: '#aaa', display: 'inline-block' }} />
                    Not Processed
                  </Box>
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <Box sx={{ width: '20px', height: '12px', border: '1.5px solid #000', borderRadius: '2px', backgroundColor: '#000', display: 'inline-block' }} />
                    Loading This Month
                  </Box>
                </Typography>
              </Box>

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', fontFamily: 'Arial, sans-serif' }}>
                <thead>
                  <tr style={{ backgroundColor: '#e8e8e8' }}>
                    {[
                      { label: 'Market',                align: 'left'   },
                      { label: '○  Under Process',      align: 'center' },
                      { label: '✕  Not Processed',      align: 'center' },
                      { label: '●  Loading This Month', align: 'center' },
                      { label: 'Total',                 align: 'center' },
                    ].map(({ label, align }) => (
                      <th key={label} style={{
                        padding: '11px 12px', textAlign: align,
                        fontWeight: 700, fontSize: '12px',
                        textTransform: 'uppercase', letterSpacing: '0.4px',
                        border: '1.5px solid #000',
                        backgroundColor: '#e0e0e0'
                      }}>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {summary.map((row, i) => (
                    <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f5f5f5' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 700, border: '1px solid #bbb', fontSize: '13px' }}>
                        {row.MARKET}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', border: '1px solid #bbb', fontSize: '14px', fontWeight: 600 }}>
                        {row['UNDER PROCESS'] || 0}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', border: '1px solid #bbb', fontSize: '14px', fontWeight: 600 }}>
                        {row['NOT PROCESSED'] || 0}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', border: '1px solid #bbb', fontSize: '14px', fontWeight: 600 }}>
                        {row['LOADING CURRENTMONTH'] || 0}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', border: '1px solid #bbb', fontSize: '15px', fontWeight: 800 }}>
                        {row.Total || 0}
                      </td>
                    </tr>
                  ))}
                  {/* Grand total */}
                  <tr style={{ backgroundColor: '#222' }}>
                    <td style={{ padding: '11px 12px', fontWeight: 800, color: '#fff', border: '1.5px solid #000', fontSize: '13px', letterSpacing: '0.5px' }}>
                      GRAND TOTAL
                    </td>
                    <td style={{ padding: '11px 12px', textAlign: 'center', fontWeight: 800, color: '#fff', border: '1.5px solid #000', fontSize: '15px' }}>
                      {gt.under}
                    </td>
                    <td style={{ padding: '11px 12px', textAlign: 'center', fontWeight: 800, color: '#fff', border: '1.5px solid #000', fontSize: '15px' }}>
                      {gt.not}
                    </td>
                    <td style={{ padding: '11px 12px', textAlign: 'center', fontWeight: 800, color: '#fff', border: '1.5px solid #000', fontSize: '15px' }}>
                      {gt.loading}
                    </td>
                    <td style={{ padding: '11px 12px', textAlign: 'center', fontWeight: 800, color: '#fff', border: '1.5px solid #000', fontSize: '16px' }}>
                      {gt.total}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Box>
          );
        })()}
      </Box>

    </Container>
  );
};

export default OrderTrackingViewV2;
